import { useCallback, useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { markOptionsWithWarningsFromKeyValue } from 'src/common/helpers/ConfigurationWarningsHelper';

import { extractLabelKey, parseLabel, TestRowSharedType } from '../helpers';
import { extractExistingLabels } from '../remove-label-tests-modal/helpers/RemoveLabelTestsModalHelpers';
import type { LabelRemoval } from '../shared-modal/LabelManagementSharedModal.types';

import { useCommonConfiguredValues } from './useCommonConfiguredValues';

interface UseLabelsRemovalProps {
	testRows: TestRowSharedType[];
}

interface UseLabelsRemovalReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	removals: LabelRemoval[];
	labelOptions: OptionType[];
	removeRemoval: (index: number) => void;
	resetRemovals: () => void;
}

export const useLabelsRemoval = ({ testRows }: UseLabelsRemovalProps): UseLabelsRemovalReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [removals, setRemovals] = useState<LabelRemoval[]>([]);
	const configuredValues = useCommonConfiguredValues();
	const labelOptions = useMemo((): OptionType[] => {
		const options = extractExistingLabels(testRows);

		return markOptionsWithWarningsFromKeyValue(
			options,
			configuredValues,
			extractLabelKey,
			parseLabel
		);
	}, [testRows, configuredValues]);

	// Auto-append the selected label to removals if new, then reset selection
	useEffect(() => {
		if (selectedLabel.length > 0) {
			const next = selectedLabel[0];
			setRemovals((prev) => {
				if (prev.some((r) => r.label.value === next.value)) {
					return prev;
				}
				return [...prev, { label: next }];
			});
			setSelectedLabel([]);
		}
	}, [selectedLabel]);

	const removeRemoval = useCallback((index: number): void => {
		setRemovals((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const resetRemovals = useCallback((): void => {
		setRemovals([]);
		setSelectedLabel([]);
	}, []);

	return {
		selectedLabel,
		setSelectedLabel,
		removals,
		labelOptions,
		removeRemoval,
		resetRemovals
	};
};
